<template>
  <State
    v-if="!files.length"
    :type="state.type"
    :title="state.title"
    :text="state.text"
    class="self-center grow"
  />
  <template v-else>
    <PassportFile
      v-for="file in files"
      :key="file.id"
      :file="file"
      :canUpdate="canUpdate"
      @preview="handlePreview"
      @unlink="handleUnlink"
    />
    <Preview
      v-if="previews.length"
      v-model:open="openPreview"
      :title="activePreview.name"
      :src="activePreview?.urls?.file || activePreview?.url"
      :type="activePreview?.mime_type"
      :multiple="previews.length > 1"
      @next="next"
      @previous="previous"
    />
  </template>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { PassportFile } from "@/SharedComponents/Passport";
import {
  MediaViewer as Preview,
  isSupportedMedia,
} from "@/SharedComponents/Media";
import { State, useState } from "@/SharedComponents/State";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["update:item"]);

const props = defineProps({
  item: { type: Object, required: true },
  canUpdate: { type: Boolean, required: false },
});

const files = computed(() =>
  props.item.files.map((file) => ({
    ...file,
    preview: isSupportedMedia(file.file.mime_type),
  }))
);

const previews = computed(() => files.value.filter((file) => file.preview));

const activeIndex = ref(0);
const openPreview = ref(undefined);

const activePreview = computed(() => previews.value[activeIndex.value].file);

function handlePreview(fileId) {
  activeIndex.value = previews.value.findIndex(
    (preview) => preview.id === fileId
  );
  openPreview.value = true;
}

function next() {
  activeIndex.value += 1;
  if (activeIndex.value === previews.value.length) {
    activeIndex.value = 0;
  }
}

function previous() {
  activeIndex.value -= 1;
  if (activeIndex.value < 0) {
    activeIndex.value = previews.value.length - 1;
  }
}

const busy = ref(false);

const { t } = useI18n();

function handleUnlink(file) {
  if (busy.value) return;

  if (!props.canUpdate) {
    toast.error(t("toasts.no_permission.assets.update"));
    return;
  }

  busy.value = true;
  axios
    .patch(route("api.files.unlink"), {
      note_ids: [file.id],
      space_ids: [props.item.id],
    })
    .then((res) => {
      toast.success(t("toasts.files.unlink.success"));
      emit("update:item", {
        item: {
          ...props.item,
          files: props.item.files.filter((f) => !res.data.ids.includes(f.id)),
          expired_files_count:
            props.item.expired_files_count - res.data.expired_count,
        },
      });
    })
    .catch((error) => {
      if (error.response.status == 403) {
        toast.error(t("toasts.files.unlink.403"));
      } else {
        toast.error(
          error.response.data.message || t("toasts.files.unlink.error")
        );
      }
    })
    .finally(() => {
      busy.value = false;
    });
}

const stateStore = useState("asset_files");
const { state } = storeToRefs(stateStore);
</script>
